/**
 * Captive-portal API client. No auth — these endpoints are public; the
 * end-user has no identity beyond their phone number.
 */

const API_BASE_URL: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
  "https://api.spotmesh.cloud";

export interface PortalContext {
  tenant: { id: string; name: string };
  router: { id: string; name: string; hotspotSubdomain: string | null };
  packages: Array<{
    id: string;
    name: string;
    durationMinutes: number;
    priceKes: number;
  }>;
}

export interface InitiateResponse {
  paymentId: string;
  voucherId: string;
  checkoutRequestId: string;
  customerMessage: string;
}

export interface PaymentStatus {
  payment: {
    id: string;
    status:
      | "initiated"
      | "callback_received"
      | "success"
      | "failed"
      | "timeout";
    darajaResultCode: number | null;
    darajaResultDesc: string | null;
    darajaMpesaReceipt: string | null;
  };
  voucher: {
    id: string;
    status: string;
    expiresAt: string | null;
  } | null;
}

async function json<T>(res: Response): Promise<T> {
  const text = await res.text();
  if (!res.ok) {
    throw new Error(`API ${res.status}: ${text.slice(0, 300)}`);
  }
  return JSON.parse(text) as T;
}

export async function getPortalContext(routerId: string): Promise<PortalContext> {
  const res = await fetch(`${API_BASE_URL}/portal/router/${routerId}`);
  return json<PortalContext>(res);
}

export async function initiatePayment(input: {
  tenantId: string;
  routerId: string;
  packageId: string;
  phone: string;
  macAddress?: string;
}): Promise<InitiateResponse> {
  const res = await fetch(`${API_BASE_URL}/payments/initiate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return json<InitiateResponse>(res);
}

export async function getPaymentStatus(paymentId: string): Promise<PaymentStatus> {
  const res = await fetch(`${API_BASE_URL}/payments/${paymentId}/status`);
  return json<PaymentStatus>(res);
}
